import { css, html, nothing } from 'lit';

const PREVIEW_API_ORIGIN = 'https://api.trinnvis.io';
import { customElement } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { PageContent } from 'src/pages/page-content.js';
import type { ScreensPageViewModel } from 'src/pages/screens-page/screens-page-view-model';
import { displayAlert } from 'src/utilities/display-alert';
import { createScreen, waitForScreenReady } from 'src/store/signage';
import { getStore } from 'src/store';
import { reloadScreens } from 'src/store/actions';
import '../d-no-items-prompt';
import 'src/library/elements/d-action';

@customElement('d-screens-page-content')
export class DScreensPageContent extends PageContent<ScreensPageViewModel> {
  static readonly styles = [
    ...super.styles,
    css`
      .screens {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        padding-top: 20px;
      }
      .screen {
        width: 100%;
        cursor: pointer;
      }
      :host(.width600) .screen {
        width: calc((100% - 12px) / 2);
      }
      .screen .visual {
        position: relative;
        border: 1px solid var(--borderColor);
        border-radius: 8px;
        aspect-ratio: 16/9;
        overflow: hidden;
        transition: transform 0.2s;
      }
      @media (hover: hover) {
        .screen .visual.preview:hover {
          transform: scale(1.05);
        }
      }
      .screen .visual img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .screen .visual iframe {
        display: block;
        width: 100%;
        height: 100%;
        border: 0;
        pointer-events: none;
      }
      .screen .label {
        display: flex;
        flex-wrap: wrap;
        margin-top: 6px;
        font-size: 15px;
      }
      .screen .label .name {
        margin-right: 4px;
        font-weight: bold;
      }
      .screen .label .hours {
        font-weight: 400;
      }
      d-action {
        margin-left: -8px;
      }
    `,
  ];

  private navigateToScreen(href: string) {
    this.dispatchEvent(
      new CustomEvent('navigate', {
        composed: true,
        bubbles: true,
        detail: { href },
      }),
    );
  }

  private get showAddScreen(): boolean {
    return this.writeAccess && !(this.pageView.screens && this.pageView.screens.some((s) => s.orderStatus === 'none'));
  }

  async onAddScreen() {
    try {
      const existingScreens = this.pageView.screens || [];
      // Gratis prøve-modus: maks én skjerm med orderStatus = 'none'. Brukeren
      // må enten bestille fysisk skjerm eller slette den eksisterende før de
      // kan legge til flere. Tilsvarende begrensning på lysbilder finnes i
      // d-slide-playlist-editor.ts:limitReached.
      if (existingScreens.some((s) => s.orderStatus === 'none')) {
        displayAlert(t('screens.freeTrialLimitAlert'));
        return;
      }
      const existingDisplayNames = existingScreens.map((s) => s.name);
      let screenName = t('screens.defaultScreenName');
      if (existingDisplayNames.includes(screenName)) {
        let screenNumber = 1;
        while (existingDisplayNames.includes(t('screens.defaultScreenNameNumbered', { number: screenNumber }))) {
          screenNumber++;
        }
        screenName = t('screens.defaultScreenNameNumbered', { number: screenNumber });
      }

      const newScreen = await createScreen(this.pageView.organizationId, screenName);
      // POST /screens returnerer displayId før skjermen er spørrbar via GET, så
      // en umiddelbar reloadScreens + navigering (→ buildScreenView.fetchScreen)
      // 404'er på den ferske skjermen. Vent til den er klar før vi fortsetter.
      await waitForScreenReady(this.pageView.organizationId, newScreen.displayId);
      await getStore().dispatch(reloadScreens(this.pageView.organizationId));
      // DScreenView.firstUpdated plukker opp dette og åpner skjermen direkte
      // i edit-mode. Bruker sessionStorage (ikke ?edit-URL) fordi
      // buildScreenView er async og willUpdate-basert URL-sjekk hadde
      // timing-problemer for dette tilfellet.
      sessionStorage.setItem('signage-edit-on-mount', String(newScreen.displayId));
      this.navigateToScreen(`${this.pageView.href}screens/${newScreen.displayId}`);
    } catch (error) {
      console.error('Failed to create screen:', error);
      displayAlert(t('screens.createScreenError'));
    }
  }
  renderContent() {
    if (!this.pageView.screens || this.pageView.screens.length === 0) {
      return html`
        <d-no-items-prompt
          entity-type="screens"
          heading="${t('screens.noScreensHeading')}"
          prompt="${t('screens.noScreensPrompt')}"
          .writeAccess=${this.writeAccess}
          @click=${() => this.onAddScreen()}
        ></d-no-items-prompt>
      `;
    }
    return html`
      <div class="screens">
        ${this.pageView.screens.map(
          (screen) => html`
            <div class="screen" @click=${() => this.navigateToScreen(screen.href)}>
              ${screen.previewUrl
                ? html`
                    <div class="visual preview">
                      <iframe
                        src="${PREVIEW_API_ORIGIN}${screen.previewUrl}"
                        title="${t('screens.livePreviewTitle')}"
                        loading="lazy"
                        sandbox="allow-scripts allow-same-origin"
                      ></iframe>
                    </div>
                  `
                : screen.screenshotUrl
                  ? html`
                      <div class="visual preview">
                        <img src="${screen.screenshotUrl}" alt="${screen.name}" />
                      </div>
                    `
                  : html`
                      <div class="visual placeholder">
                        <div>${t('screens.noPreview')}</div>
                      </div>
                    `}
              <div class="label">
                <div class="name">${screen.name}</div>
                <div class="hours">${screen.schedule ?? ''}</div>
              </div>
            </div>
          `,
        )}
      </div>
      ${this.showAddScreen
        ? html` <d-action plain @click=${() => this.onAddScreen()}> ${t('screens.addNewScreen')} </d-action> `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-screens-page-content': DScreensPageContent;
  }
}
